import React from "react";
import { Link } from "react-router-dom";
import {
  LegalLayout,
  LegalH2,
  LegalP,
  LegalUl
} from "../../components/landing/LegalLayout";

export default function NutzerbedingungenEn() {
  return (
    <LegalLayout
      testId="page-nutzerbedingungen"
      title="Terms of Use"
      subtitle="Terms of use for the «WasGitsHüt?» app by Liberox GmbH."
    >
      <LegalP>Last updated: 21 September 2026</LegalP>

      <LegalH2>1. Provider and scope</LegalH2>
      <LegalP>
        These terms of use govern the use of the WasGitsHüt? mobile application
        and the associated services. WasGitsHüt? is provided by Liberox GmbH,
        based in Switzerland.
      </LegalP>
      <LegalP>
        By creating an account or using WasGitsHüt? you agree to these terms of
        use. Insofar as individual features are offered via the Apple App Store or
        Google Play, the respective terms of the relevant platform additionally
        apply.
      </LegalP>

      <LegalH2>2. Purpose and features of the app</LegalH2>
      <LegalP>
        WasGitsHüt? supports individuals and households in planning, choosing and
        preparing meals. Depending on the current version, the following features
        in particular may be offered:
      </LegalP>
      <LegalUl
        items={[
          "personalised recipe suggestions",
          "joint voting within a household",
          "management of likes, dislikes, diets and allergies",
          "recognition of ingredients from images",
          "favourites and saved recipes",
          "shopping lists",
          "cooking mode and step-by-step instructions",
          "display of nutritional information",
          "selection of various cuisines and food cultures",
          "location features for finding stores nearby",
          "content and recipe suggestions supported by artificial intelligence"
        ]}
      />
      <LegalP>
        The specific range of features may vary depending on the operating
        system, app version, region and chosen subscription.
      </LegalP>

      <LegalH2>3. User account</LegalH2>
      <LegalP>
        A user account is required for the essential features of WasGitsHüt?.
        Depending on the available app version, registration may take place for
        example via an e-mail address, Apple or Google.
      </LegalP>
      <LegalP>
        You are responsible for adequately protecting your login credentials and
        for not granting unauthorised persons access to your account. If you
        suspect misuse or unauthorised access to your account, you should contact
        us without delay.
      </LegalP>
      <LegalP>
        Persons who, due to their age or for other legal reasons, cannot validly
        consent to these terms of use themselves may only use WasGitsHüt? with the
        consent of their legal representative.
      </LegalP>

      <LegalH2>4. Households and shared use</LegalH2>
      <LegalP>
        WasGitsHüt? enables the shared use of certain features within a household.
        Depending on the feature, members of a household may see, for example,
        votes, recipe decisions, shopping lists and certain profile settings of
        other household members.
      </LegalP>
      <LegalP>
        Anyone who invites other people to a household is responsible for only
        passing invitations to people who are actually meant to be members of the
        relevant household. Information shared within a household may be visible to
        other members of that household. Users should therefore only store
        information intended for shared use.
      </LegalP>

      <LegalH2>5. User content and images</LegalH2>
      <LegalP>
        You generally remain responsible for content that you enter or upload into
        WasGitsHüt? yourself. This may include, for example, images of food,
        shopping lists, details of preferences or other self-entered information.
        You may only use content that you are entitled to use.
      </LegalP>
      <LegalP>
        By uploading or entering content you grant Liberox GmbH the right required
        to provide the respective app feature, namely to store, process and
        display this content technically. This right of use is limited to the
        operation and provision of WasGitsHüt?.
      </LegalP>

      <LegalH2>6. Artificial intelligence and automatically generated content</LegalH2>
      <LegalP>
        Individual features of WasGitsHüt? use artificial intelligence or other
        automated systems. Recipes, ingredient lists, images, quantities,
        nutritional values or recommendations generated in this way may contain
        errors. Automatically generated content must therefore be checked
        independently before use.
      </LegalP>
      <LegalP>
        In particular, we do not warrant that automatically generated content is
        complete, error-free or suitable for a specific personal purpose.
        Automatically generated content may also be similar or identical to
        content shown to other users. Further information about the processing of
        data in connection with such features can be found in our{" "}
        <Link to="/datenschutz" className="font-semibold text-terracotta hover:underline">
          privacy policy
        </Link>
        .
      </LegalP>

      <LegalH2>7. Allergies, intolerances and health</LegalH2>
      <LegalP>
        WasGitsHüt? is not a medical or nutritional advisory service. Information
        about allergies, intolerances, diets or personal preferences serves to
        make recipe suggestions as suitable as possible.
      </LegalP>
      <LegalP>
        Despite such information, it cannot be guaranteed that a recipe does not
        contain problematic ingredients or allergens. You are therefore
        responsible yourself for checking ingredients, product labels and
        allergens before consumption.
      </LegalP>
      <LegalP>
        In the case of severe allergies, medically necessary diets or comparable
        health requirements, you must not rely solely on information or
        suggestions within the app. For health questions, an appropriately
        qualified professional should be consulted.
      </LegalP>

      <LegalH2>8. Nutritional values and quantities</LegalH2>
      <LegalP>
        Calories, protein, fat, carbohydrates and other nutritional values may be
        calculated, estimated or derived from available product information. Such
        information is for guidance only and may deviate from the actual values of
        a meal. Portion sizes, cooking times and ingredient quantities may also
        vary depending on the products used and the preparation.
      </LegalP>

      <LegalH2>9. Location and information about stores</LegalH2>
      <LegalP>
        If you use the location feature, WasGitsHüt? may use your location, for
        example to display stores nearby. Information about locations, opening
        hours, availability or other details of stores may change. We therefore
        accept no warranty that information about third parties is complete or up
        to date at all times.
      </LegalP>

      <LegalH2>10. Free use and premium features</LegalH2>
      <LegalP>
        WasGitsHüt? can be downloaded for free. Certain features may be available
        for free only to a limited extent. Currently, users may for example
        receive a certain number of free cooking rounds. The applicable free range
        of features is displayed within the app. In addition, paid premium
        features may be offered.
      </LegalP>

      <LegalH2>11. Subscriptions</LegalH2>
      <LegalP>
        WasGitsHüt? offers automatically renewing subscriptions. Depending on the
        platform, subscriptions with a term of one month, six months or one year
        may in particular be available. The binding price, the term and the
        specific scope of services are displayed before the purchase is completed
        within the app and in the respective app store.
      </LegalP>
      <LegalP>
        Subscriptions are billed via the Apple App Store or Google Play. Liberox
        GmbH generally does not receive your full credit card or bank details.
      </LegalP>

      <LegalH2>12. Automatic renewal</LegalH2>
      <LegalP>
        A subscription renews automatically for the respectively agreed term
        unless it is cancelled in good time in accordance with the rules of the
        Apple App Store or Google Play. The management and cancellation of the
        subscription is carried out via the user account of the respective
        platform.
      </LegalP>
      <LegalP>
        A cancellation generally ends the automatic renewal. Already paid premium
        features normally remain available until the end of the already paid
        subscription period.
      </LegalP>

      <LegalH2>13. Refunds</LegalH2>
      <LegalP>
        Payments via the Apple App Store or Google Play are processed by the
        respective platform. Refund requests must therefore generally be submitted
        via Apple or Google and are assessed in accordance with the rules
        applicable there. Mandatory statutory rights of users are reserved.
      </LegalP>

      <LegalH2>14. Account deletion and existing subscriptions</LegalH2>
      <LegalP>
        You can delete your user account in accordance with the options offered
        within the app. Deleting the WasGitsHüt? account does not automatically
        cancel an Apple or Google subscription. An existing subscription must
        therefore be cancelled separately via the Apple App Store or Google Play.
      </LegalP>
      <LegalP>
        After an account is deleted, data may be deleted or retained for legally
        required periods in accordance with our{" "}
        <Link to="/datenschutz" className="font-semibold text-terracotta hover:underline">
          privacy policy
        </Link>
        .
      </LegalP>

      <LegalH2>15. Family sharing</LegalH2>
      <LegalP>
        Insofar as Apple, Google or another platform supports family sharing for a
        particular subscription, a subscription may be shared with eligible family
        members in accordance with the respective platform rules. Whether and
        under which conditions family sharing is possible depends on the settings
        and rules of the respective platform.
      </LegalP>

      <LegalH2>16. Permitted use</LegalH2>
      <LegalP>
        WasGitsHüt? may only be used within the scope of the intended features. In
        particular, it is prohibited to:
      </LegalP>
      <LegalUl
        items={[
          "use the app for unlawful or fraudulent purposes",
          "improperly interfere with other users or households",
          "access accounts, systems or data of other people without authorisation",
          "circumvent security mechanisms",
          "deliberately disrupt or overload the app or its infrastructure",
          "use automated systems for abusive use of the service",
          "distribute malware or comparable harmful content",
          "infringe the rights of third parties",
          "copy, modify or technically investigate the app contrary to mandatory statutory provisions"
        ]}
      />
      <LegalP>
        In the case of serious or repeated violations, we may restrict or block
        access to individual features or to the user account.
      </LegalP>

      <LegalH2>17. Rights in WasGitsHüt?</LegalH2>
      <LegalP>
        The app, the name WasGitsHüt?, the design, the software, graphics, texts
        and other content provided by us are legally protected where applicable.
        Using the app does not grant you any ownership of these components. You
        receive a personal, limited and non-transferable right to use WasGitsHüt?
        in accordance with these terms of use.
      </LegalP>

      <LegalH2>18. Availability and changes to the app</LegalH2>
      <LegalP>
        We strive for a reliable operation of WasGitsHüt?. However, availability at
        all times and completely free of disruption cannot be guaranteed.
        Temporary restrictions may occur in particular due to maintenance work,
        technical faults, security measures or failures of external services.
      </LegalP>
      <LegalP>
        We may further develop, change, replace or remove features, provided that
        this does not unduly impair rights already acquired and mandatory
        statutory claims.
      </LegalP>

      <LegalH2>19. Liability</LegalH2>
      <LegalP>
        Use of WasGitsHüt? takes place at your own responsibility within the
        limits permitted by law. Liberox GmbH is liable without limitation insofar
        as a limitation of liability is not legally permissible.
      </LegalP>
      <LegalP>
        Insofar as legally permissible, liability for slight negligence as well as
        for indirect and consequential damages is excluded. A limitation of
        liability does not apply in particular to damages caused intentionally or
        through gross negligence, or to other cases in which a limitation of
        liability is legally excluded.
      </LegalP>
      <LegalP>
        For recipes, allergens, nutritional information and automatically generated
        content, the user's obligation to carry out an independent check remains in
        place.
      </LegalP>

      <LegalH2>20. Data protection</LegalH2>
      <LegalP>
        The processing of personal data is governed by our privacy policy. It
        explains in particular which data is processed, for which purposes this is
        done and which rights users have. The privacy policy is available at the
        following address:{" "}
        <Link to="/datenschutz" className="font-semibold text-terracotta hover:underline">
          https://wasgitshüt.ch/datenschutz
        </Link>
      </LegalP>
      <LegalP>
        Health-related information may constitute particularly sensitive personal
        data under Swiss data protection law. Correspondingly increased
        requirements for transparency and protection apply to such data.
      </LegalP>

      <LegalH2>21. Apple App Store</LegalH2>
      <LegalP>
        If WasGitsHüt? is obtained via the Apple App Store, the following
        provisions additionally apply:
      </LegalP>
      <LegalUl
        items={[
          "The contract for the use of WasGitsHüt? is between you and Liberox GmbH and not with Apple.",
          "Apple is not responsible for the operation, content, support or maintenance of WasGitsHüt?.",
          "Use of the app is limited to Apple devices and the use permitted under the Apple usage rules.",
          "Insofar as legally permissible, Liberox GmbH and not Apple is responsible for claims in connection with the app.",
          "Apple and its affiliated companies are, within the scope of the provisions required for the App Store, third-party beneficiaries of these terms of use and can enforce the relevant provisions.",
          "In addition, the respective current terms of the Apple App Store apply."
        ]}
      />

      <LegalH2>22. Google Play</LegalH2>
      <LegalP>
        If WasGitsHüt? is obtained via Google Play, the terms of Google Play
        additionally apply. Purchases, subscriptions, cancellations and refunds may
        be processed in accordance with the respective Google Play rules.
      </LegalP>

      <LegalH2>23. Changes to these terms of use</LegalH2>
      <LegalP>
        We may adjust these terms of use where this is necessary, for example due
        to new features, legal requirements, security requirements or material
        changes to our offering. We will inform you of material changes in an
        appropriate manner. If a renewed consent is legally required due to a
        change, we will obtain this consent.
      </LegalP>

      <LegalH2>24. Applicable law and place of jurisdiction</LegalH2>
      <LegalP>
        These terms of use are governed by Swiss law. Mandatory statutory
        protective provisions for consumers are reserved. Insofar as legally
        permissible and no mandatory place of jurisdiction exists, the place of
        jurisdiction is the registered office of Liberox GmbH.
      </LegalP>
      <LegalP>
        For consumers, mandatory places of jurisdiction at their own domicile may
        in particular exist, which cannot be validly waived in advance. This
        restriction is especially important for international users.
      </LegalP>

      <LegalH2>25. Final provisions</LegalH2>
      <LegalP>
        Should an individual provision of these terms of use be invalid or
        unenforceable, the remaining provisions generally remain unaffected. In
        place of an invalid provision, the applicable statutory rules apply.
        Mandatory rights to which users are entitled under applicable law are not
        restricted by these terms of use.
      </LegalP>

      <LegalH2>26. Contact</LegalH2>
      <LegalP>
        For questions about WasGitsHüt? or these terms of use, you can use the
        contact options provided on our website or within the app.
      </LegalP>
      <LegalUl
        items={[
          "Provider: Liberox GmbH, Switzerland",
          "Website: https://wasgitshüt.ch"
        ]}
      />
    </LegalLayout>
  );
}
